import { ConfigProvider, Flex, Select, Switch, Typography, message } from 'antd';
import { useState } from 'react';

import { useUpdateBillingAutoRecharge } from '../../../api/generated/api';
import { TextTertiary } from '../../../ui/components/text';
import { formatDate, formatDateTime } from '../../../ui/utils/format-date';
import { useRefreshBillingContext } from '../hooks/useRefreshBillingContext';
import { formatCredits, formatCreditsShort } from '../shared/billing-format';
import { formatDiscountedUsd } from '../shared/discount';

import { AutoRechargeModal } from './AutoRechargeModal';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';

export type AutoRechargeSettingProps = {
  organizationId: string;
  overview: GetBillingOverviewResponseDto;
  portalLoading: boolean;
  onOpenPortal: () => void;
};

export const AutoRechargeSetting = ({
  organizationId,
  overview,
  portalLoading,
  onOpenPortal,
}: AutoRechargeSettingProps) => {
  const refresh = useRefreshBillingContext();
  const [modalOpen, setModalOpen] = useState(false);
  const updateMutation = useUpdateBillingAutoRecharge();

  const { autoRecharge, topUp } = overview;
  if (!autoRecharge || !topUp) {
    return null;
  }

  const { enabled, pack, blockedReason } = autoRecharge;
  const blocked =
    blockedReason &&
    {
      locked: `Paused by K3X support${autoRecharge.lockedUntil ? ` until ${formatDateTime(autoRecharge.lockedUntil)}` : ''}.`,
      no_subscription: 'Starts with your subscription.',
      trial: 'Starts with your paid plan.',
      inactive: 'Starts once your plan is active.',
      cancellation_pending: 'Off while your plan is scheduled to end.',
      dispute: 'Not available while a payment dispute is open.',
    }[blockedReason];

  const rule = `When you drop below ${formatCredits(autoRecharge.thresholdCredits)} credits, we add ${formatCredits(pack.credits)} credits for ${formatDiscountedUsd(pack.priceCents, pack.discountedPriceCents)}.`;
  const declined = !enabled && autoRecharge.disabledAt !== null && !blockedReason;

  const update = (data: { enabled: boolean; priceId?: string }) =>
    updateMutation.mutate(
      { organizationId, data },
      {
        onSuccess: () => refresh(organizationId),
        onError: () => message.error("Couldn't update auto-recharge"),
      },
    );

  return (
    <>
      <Flex
        align="center"
        gap={14}
        wrap
        data-testid="auto-recharge-setting"
        style={{
          padding: '14px 18px',
          borderTop: '1px solid var(--ant-color-split)',
          background: 'var(--ant-color-fill-alter)',
        }}
      >
        <Flex vertical gap={2} style={{ flex: '1 1 200px' }}>
          <Typography.Text style={{ fontSize: 13, fontWeight: 600 }}>
            Auto-recharge · {enabled ? 'On' : 'Off'}
          </Typography.Text>
          {declined ? (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              Turned off on {formatDate(autoRecharge.disabledAt)} because the card was declined.
              Update your card, then turn it back on.
            </Typography.Text>
          ) : (
            <TextTertiary style={{ fontSize: 12 }}>
              {!enabled && blocked ? blocked : [rule, blocked].filter(Boolean).join(' ')}
            </TextTertiary>
          )}
          {autoRecharge.pausedUntil && (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              Paused until {formatDateTime(autoRecharge.pausedUntil)}. It ran{' '}
              {autoRecharge.dailyCap} times today.
            </Typography.Text>
          )}
        </Flex>
        <Select
          value={pack.priceId}
          onChange={(priceId) => update({ enabled, priceId })}
          disabled={updateMutation.isPending || (!enabled && blockedReason !== null)}
          popupMatchSelectWidth={false}
          data-testid="auto-recharge-change-pack"
          options={topUp.packs.map((option) => ({
            value: option.priceId,
            label: `${formatCreditsShort(option.credits)} credits · ${formatDiscountedUsd(option.priceCents, option.discountedPriceCents)}`,
          }))}
          style={{ height: 36, fontSize: 13, fontWeight: 500 }}
          styles={{ root: { borderRadius: 12 } }}
        />
        <ConfigProvider
          theme={{ components: { Switch: { trackHeight: 20, trackMinWidth: 36, handleSize: 16 } } }}
        >
          <Switch
            checked={enabled}
            loading={updateMutation.isPending}
            disabled={!enabled && blockedReason !== null}
            onChange={(checked) => (checked ? setModalOpen(true) : update({ enabled: false }))}
            data-testid={enabled ? 'auto-recharge-turn-off' : 'auto-recharge-turn-on'}
          />
        </ConfigProvider>
      </Flex>
      <AutoRechargeModal
        open={modalOpen}
        organizationId={organizationId}
        overview={overview}
        portalLoading={portalLoading}
        onOpenPortal={onOpenPortal}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
